import { apiClient } from './client'

export interface Session {
  _id: string
  id?: string // Alias for _id, some API responses include this
  institutionId: string
  lessonId: string
  teacherId: string
  streamIds: string[]
  title: string
  description?: string
  scheduledStartTime: string
  scheduledEndTime: string
  actualStartTime?: string
  actualEndTime?: string
  location?: string
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'missed'
  attendance?: {
    studentId: string
    status: 'present' | 'absent' | 'late'
    checkInTime?: string
    checkOutTime?: string
    attendanceHours?: number // Hours attended for this student
  }[]
  sessionDurationHours?: number // Total session duration in hours
  notes?: string
  resources?: string[]
  isActive: boolean
  createdById: string
  createdAt: string
  updatedAt: string
  // Populated fields
  lesson?: {
    id: string
    title: string
    description?: string
    duration: number
    objectives?: string
    materials?: string
  }
  teacher?: {
    id: string
    firstName: string
    lastName: string
    email: string
  }
  streams?: {
    id: string
    name: string
    gradeId: string
  }[]
  attendanceCount?: number
  presentCount?: number
  attendanceDetails?: {
    studentId: string
    status: 'present' | 'absent' | 'late'
    checkInTime?: string
    checkOutTime?: string
    attendanceHours?: number
    student?: {
      id: string
      firstName: string
      lastName: string
      email: string
    }
  }[]
}

export interface CreateSessionRequest {
  lessonId: string
  teacherId: string
  streamIds: string[]
  title: string
  description?: string
  scheduledStartTime: string
  scheduledEndTime: string
  location?: string
  institutionId?: string // For super admins
}

export interface UpdateSessionRequest {
  title?: string
  description?: string
  scheduledStartTime?: string
  scheduledEndTime?: string
  location?: string
  status?: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'missed'
  notes?: string
  resources?: string[]
  attendance?: {
    studentId: string
    status: 'present' | 'absent' | 'late'
    checkInTime?: string
    checkOutTime?: string
    attendanceHours?: number
  }[]
  sessionDurationHours?: number
}

export interface GetSessionsParams {
  page?: number
  limit?: number
  search?: string
  teacherId?: string
  lessonId?: string
  status?: string
  startDate?: string
  endDate?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  institutionId?: string // For super admins
}

export interface SessionStats {
  totalSessions: number
  upcomingSessions: number
  todaysSessions: number
  scheduled: number
  inProgress: number
  completed: number
  cancelled: number
  missed: number
}

// Get all sessions with optional filtering and pagination
export const getSessions = async (params: GetSessionsParams = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/sessions?${searchParams.toString()}`)
  return response.data
}

// Get single session by ID
export const getSession = async (id: string) => {
  const response = await apiClient.get(`/sessions/${id}`)
  return response.data
}

// Create new session
export const createSession = async (sessionData: CreateSessionRequest) => {
  const response = await apiClient.post('/sessions', sessionData)
  return response.data
}

// Update existing session
export const updateSession = async (id: string, sessionData: UpdateSessionRequest) => {
  const response = await apiClient.put(`/sessions/${id}`, sessionData)
  return response.data
}

// Delete session (soft delete)
export const deleteSession = async (id: string) => {
  const response = await apiClient.delete(`/sessions/${id}`)
  return response.data
}

// Start a session (for teachers)
export const startSession = async (id: string) => {
  const response = await apiClient.post(`/sessions/${id}/start`)
  return response.data
}

// End a session (for teachers)
export const endSession = async (id: string, notes?: string) => {
  const response = await apiClient.post(`/sessions/${id}/end`, { notes })
  return response.data
}

// Get session statistics
export const getSessionStats = async (institutionId?: string) => {
  const params = institutionId ? `?institutionId=${institutionId}` : ''
  const response = await apiClient.get(`/sessions/stats${params}`)
  return response.data
}

// Quick session creation from lesson (for lessons page) - starts immediately
export const createSessionFromLesson = async (lessonId: string, teacherId: string, streamIds: string[], lessonTitle: string, lessonDescription?: string, lessonDuration: number = 45) => {
  try {
    // Create session with lesson data - use the create-and-start endpoint directly
    const sessionData: CreateSessionRequest = {
      lessonId,
      teacherId,
      streamIds: streamIds,
      title: lessonTitle,
      description: lessonDescription,
      scheduledStartTime: new Date().toISOString(), // Start now
      scheduledEndTime: new Date(Date.now() + (lessonDuration * 60 * 1000)).toISOString(), // End after lesson duration
    }
    
    // Use the direct create-and-start endpoint
    return createAndStartSession(sessionData)
  } catch (error) {
    console.error('Failed to create and start session from lesson:', error)
    throw error
  }
}

// Create and immediately start a session
export const createAndStartSession = async (sessionData: CreateSessionRequest) => {
  console.log('Creating and starting session with data:', sessionData)
  const response = await apiClient.post('/sessions/create-and-start', sessionData)
  console.log('Session created and started:', response.data)
  return response.data
}

// Get upcoming sessions for a teacher
export const getTeacherUpcomingSessions = async (teacherId: string, limit: number = 5) => {
  const params: GetSessionsParams = {
    teacherId,
    status: 'scheduled',
    sortBy: 'scheduledStartTime',
    sortOrder: 'asc',
    limit,
    startDate: new Date().toISOString()
  }
  
  return getSessions(params)
}

// Get active sessions for a teacher
export const getTeacherActiveSessions = async (teacherId: string) => {
  const params: GetSessionsParams = {
    teacherId,
    status: 'in_progress',
    sortBy: 'actualStartTime',
    sortOrder: 'asc'
  }
  
  return getSessions(params)
}

// Get active session for a specific lesson
export const getActiveLessonSession = async (lessonId: string) => {
  const params: GetSessionsParams = {
    lessonId,
    status: 'in_progress',
    limit: 1
  }
  
  const response = await getSessions(params)
  return response.data.sessions.length > 0 ? response.data.sessions[0] : null
}

// Check if a lesson has an active session
export const hasActiveSession = async (lessonId: string): Promise<boolean> => {
  const activeSession = await getActiveLessonSession(lessonId)
  return activeSession !== null
}

// Get teacher's assigned sessions
export const getTeacherSessions = async (params: {
  page?: number
  limit?: number
  status?: string
  dateFrom?: string
  dateTo?: string
  includeAttendance?: boolean
} = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/sessions/my-sessions?${searchParams.toString()}`)
  return response.data
}

// Get student's lessons/sessions
export const getStudentLessons = async (params: {
  page?: number
  limit?: number
  status?: string
  dateFrom?: string
  dateTo?: string
} = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/sessions/my-lessons?${searchParams.toString()}`)
  return response.data
}

// Get session with paginated attendance
export const getSessionAttendance = async (sessionId: string, params: {
  attendancePage?: number
  attendanceLimit?: number
  attendanceStatus?: string
  attendanceSearch?: string
} = {}) => {
  const searchParams = new URLSearchParams()
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  
  const response = await apiClient.get(`/attendance/session/${sessionId}?${searchParams.toString()}`)
  return response.data
}

// Check-in student to session
export const checkInStudent = async (checkInData: {
  sessionId: string
  userId: string
  terminalId: string
  checkInType?: 'rfid' | 'biometric' | 'manual' | 'qr_code'
  notes?: string
}) => {
  const response = await apiClient.post('/attendance/check-in', checkInData)
  return response.data
}

// Check-out student from session
export const checkOutStudent = async (checkOutData: {
  sessionId: string
  userId: string
  terminalId: string
  checkOutType?: 'rfid' | 'biometric' | 'manual' | 'qr_code'
  notes?: string
}) => {
  const response = await apiClient.post('/attendance/check-out', checkOutData)
  return response.data
}